/**
 * Users Page Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Main page for user management with list, search, and CRUD operations.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Search, UserPlus, Filter, X } from 'lucide-react';
import { useAccount } from '../../contexts/AccountContext';
import { userManagementService } from '../../services/admin';
import type {
  UserListItem,
  UserListResponse,
  UserRole,
  UserCreateData,
  UserUpdateData,
} from '../../types/admin';
import { ROLE_LABELS } from '../../types/admin';
import {
  UsersList,
  UserFormModal,
  InviteUserModal,
  ChangeRoleModal,
  UserClientsModal,
} from '../../components/admin/users';
import { Button } from '../../components/ui/button';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../utils/apiErrorHandler';

const UsersPage: React.FC = () => {
  const { user: currentUser } = useAccount();

  // State
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | ''>('');
  const [showFilters, setShowFilters] = useState(false);

  // Modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showClientsModal, setShowClientsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Confirmation dialogs
  const [confirmAction, setConfirmAction] = useState<{
    type: 'delete' | 'status';
    user: UserListItem;
  } | null>(null);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response: UserListResponse = await userManagementService.listUsers({
        search: searchQuery || undefined,
        role: roleFilter || undefined,
        is_active: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
        page,
        page_size: pageSize,
      });
      setUsers(response.users);
      setTotalUsers(response.total);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, roleFilter, statusFilter, page, pageSize]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handlers
  const handleInviteUser = async (data: { email: string; role: UserRole }) => {
    setIsSubmitting(true);
    try {
      await userManagementService.inviteUser(data);
      toast.success(`Invitation sent to ${data.email}`);
      setShowInviteModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to send invitation'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateOrUpdateUser = async (data: UserCreateData | UserUpdateData) => {
    setIsSubmitting(true);
    try {
      if (selectedUser) {
        await userManagementService.updateUser(selectedUser.id, data as UserUpdateData);
        toast.success('User updated successfully');
      } else {
        await userManagementService.createUser(data as UserCreateData);
        toast.success('User created successfully');
      }
      setShowUserModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to save user'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeRole = async (userId: string, role: UserRole) => {
    setIsSubmitting(true);
    try {
      await userManagementService.changeUserRole(userId, { role });
      toast.success('Role changed successfully');
      setShowRoleModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to change role'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (user: UserListItem) => {
    try {
      await userManagementService.changeUserStatus(user.id, { is_active: !user.is_active });
      toast.success(user.is_active ? 'User deactivated' : 'User activated');
      setConfirmAction(null);
      fetchUsers();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to change user status'));
    }
  };

  const handleDeleteUser = async (user: UserListItem) => {
    try {
      await userManagementService.deleteUser(user.id);
      toast.success('User deleted successfully');
      setConfirmAction(null);
      fetchUsers();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to delete user'));
    }
  };

  const handleResendInvite = async (user: UserListItem) => {
    try {
      await userManagementService.resendInvitation(user.id);
      toast.success(`Invitation resent to ${user.email}`);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to resend invitation'));
    }
  };

  const handleResetPassword = async (user: UserListItem) => {
    try {
      await userManagementService.forcePasswordReset(user.id);
      toast.success(`Password reset email sent to ${user.email}`);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to reset password'));
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setRoleFilter('');
    setStatusFilter('');
    setPage(1);
  };

  const hasActiveFilters = searchQuery || roleFilter || statusFilter;
  const totalPages = Math.ceil(totalUsers / pageSize);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-500 mt-1">
            Manage user accounts, roles, and permissions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => {
              setSelectedUser(null);
              setShowUserModal(true);
            }}
            variant="outline"
          >
            <UserPlus size={18} className="mr-2" />
            Create User
          </Button>
          <Button onClick={() => setShowInviteModal(true)}>
            <UserPlus size={18} className="mr-2" />
            Invite User
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent"
            />
          </div>

          {/* Filter toggle */}
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? 'bg-gray-100' : ''}
          >
            <Filter size={18} className="mr-2" />
            Filters
            {hasActiveFilters && (
              <span className="ml-2 w-5 h-5 rounded-full bg-[#002B45] text-white text-xs flex items-center justify-center">
                {[roleFilter, statusFilter].filter(Boolean).length}
              </span>
            )}
          </Button>

          {/* Clear filters */}
          {hasActiveFilters && (
            <Button variant="ghost" onClick={clearFilters} size="sm">
              <X size={16} className="mr-1" />
              Clear
            </Button>
          )}
        </div>

        {/* Expanded filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-4">
            {/* Role filter */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Role
              </label>
              <select
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value as UserRole | '');
                  setPage(1);
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
              >
                <option value="">All roles</option>
                {(['admin', 'advisor', 'viewer', 'compliance_officer'] as UserRole[]).map((role) => (
                  <option key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </option>
                ))}
              </select>
            </div>

            {/* Status filter */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as 'active' | 'inactive' | '');
                  setPage(1);
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="text-sm text-gray-500">
        {isLoading ? (
          'Loading...'
        ) : (
          <>
            Showing {users.length} of {totalUsers} users
            {hasActiveFilters && ' (filtered)'}
          </>
        )}
      </div>

      {/* Users list */}
      <UsersList
        users={users}
        isLoading={isLoading}
        currentUserId={currentUser?.id}
        onEdit={(user) => {
          setSelectedUser(user);
          setShowUserModal(true);
        }}
        onChangeRole={(user) => {
          setSelectedUser(user);
          setShowRoleModal(true);
        }}
        onToggleStatus={(user) => setConfirmAction({ type: 'status', user })}
        onDelete={(user) => setConfirmAction({ type: 'delete', user })}
        onResendInvite={handleResendInvite}
        onResetPassword={handleResetPassword}
        onManageClients={(user) => {
          setSelectedUser(user);
          setShowClientsModal(true);
        }}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* Modals */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSubmit={handleInviteUser}
        isLoading={isSubmitting}
      />

      <UserFormModal
        isOpen={showUserModal}
        onClose={() => {
          setShowUserModal(false);
          setSelectedUser(null);
        }}
        onSubmit={handleCreateOrUpdateUser}
        user={selectedUser}
        isLoading={isSubmitting}
      />

      <ChangeRoleModal
        isOpen={showRoleModal}
        onClose={() => {
          setShowRoleModal(false);
          setSelectedUser(null);
        }}
        onSubmit={handleChangeRole}
        user={selectedUser}
        isLoading={isSubmitting}
      />

      <UserClientsModal
        isOpen={showClientsModal}
        onClose={() => {
          setShowClientsModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />

      {/* Confirmation dialogs */}
      <ConfirmDialog
        open={confirmAction?.type === 'delete'}
        onOpenChange={(open) => !open && setConfirmAction(null)}
        title="Delete User"
        description={`Are you sure you want to delete ${confirmAction?.user.display_name || confirmAction?.user.email}? This action cannot be undone.`}
        onConfirm={() => confirmAction && handleDeleteUser(confirmAction.user)}
        confirmText="Delete"
        destructive
      />

      <ConfirmDialog
        open={confirmAction?.type === 'status'}
        onOpenChange={(open) => !open && setConfirmAction(null)}
        title="Change Status"
        description={`Are you sure you want to ${confirmAction?.user.is_active ? 'deactivate' : 'activate'} ${confirmAction?.user.display_name || confirmAction?.user.email}?`}
        onConfirm={() => confirmAction && handleToggleStatus(confirmAction.user)}
        confirmText={confirmAction?.user.is_active ? 'Deactivate' : 'Activate'}
      />
    </div>
  );
};

export default UsersPage;
